import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    Home, Settings, LogOut, User,
    ChevronRight, ChevronLeft, MapPin, FileText, DollarSign, X
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useBrandedImages } from '../../hooks/useBranding';

const Sidebar = ({ isOpen, setIsOpen, isMobile = false, onNavigate }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const { loginLogo, getSidebarLogo } = useBrandedImages();

    const menuItems = [
        { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/dashboard' },
        { id: 'services', label: 'Services', icon: Settings, path: '/services' },
        { id: 'locations', label: 'Locations', icon: MapPin, path: '/locations' },
        { id: 'service-rates', label: 'Service Rates', icon: DollarSign, path: '/service-rates' },
        { id: 'invoices', label: 'Invoices', icon: FileText, path: '/invoices' }
    ];

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const showLabels = isMobile || isOpen;
    // Same asset as login when expanded; compact icon when collapsed
    const logoSrc = showLabels ? (loginLogo || getSidebarLogo(true)) : getSidebarLogo(false);

    const renderMenuItem = (item) => {
        const Icon = item.icon;
        return (
            <li key={item.id}>
                <NavLink
                    to={item.path}
                    onClick={() => onNavigate?.()}
                    className={({ isActive }) => `
                        w-full flex items-center gap-4 px-5 py-2 rounded-lg transition-colors
                        ${isActive
                            ? 'bg-gray-600 text-white rounded-sm'
                            : 'text-white hover:bg-gray-600 rounded-sm hover:text-white'
                        }
                    `}
                >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {showLabels && <span>{item.label}</span>}
                </NavLink>
            </li>
        );
    };

    const widthClass = isMobile
        ? 'w-64'
        : (isOpen ? 'w-64' : 'w-20');

    const mobilePositionClass = isMobile
        ? `fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`
        : 'relative shrink-0';

    return (
        <div className={`${widthClass} ${mobilePositionClass} sidebarbg transition-all duration-300 flex flex-col h-full`}>
            <nav className="flex-1 px-4 pb-4 plr-0 navbgMain overflow-y-auto">
                <div className="p-3">
                    <div className="logoheader relative flex items-center justify-center">
                        <div className="flex items-center justify-center w-full">
                            {logoSrc && (
                                <img
                                    src={logoSrc}
                                    alt="Company Logo"
                                    className={`object-contain block transition-all duration-300 ${
                                        showLabels
                                            ? 'h-10 w-auto max-w-[180px]'
                                            : 'h-8 w-8'
                                    }`}
                                />
                            )}

                            {isMobile ? (
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="text-gray-600 hover:text-gray-900 transition-colors absolute right-1 top-1/2 -translate-y-1/2 p-1 z-10"
                                    aria-label="Close menu"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(!isOpen)}
                                    className="text-gray-400 hover:text-white transition-colors colose-btn"
                                    aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                                >
                                    {isOpen
                                        ? <ChevronLeft className="w-7 h-7 close-right" />
                                        : <ChevronRight className="w-7 h-7 open-right" />}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <ul className="space-y-2 p-3">
                    {menuItems.map(item => renderMenuItem(item))}
                </ul>

                <div className="p-4">
                    <div className={`flex items-center gap-3 mb-3 ${!showLabels && 'justify-center'}`}>
                        <div className="w-8 h-8 bg-scarlet-600 rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                        </div>
                        {showLabels && (
                            <div className="flex-1 min-w-0">
                                <p className="text-scarlet text-sm font-medium truncate">{user?.name || 'Vendor'}</p>
                                <p className="text-gray-400 text-xs capitalize">{user?.role || 'Vendor'}</p>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={handleLogout}
                        className={`mt-4 w-full flex items-center rounded-sm gap-3 px-3 py-2 text-white hover:text-white transition-colors ${!showLabels && 'justify-center'}`}
                    >
                        <LogOut className="w-5 h-5" />
                        {showLabels && <span>Logout</span>}
                    </button>
                </div>
            </nav>
        </div>
    );
};

export default Sidebar;
